import React from "react";

import { useNavigate } from "react-router-dom";

import {
  ChevronDown,
  ChevronUp,
  Mail,
  Phone,
  ShieldAlert,
  ShieldCheck,
  User,
} from "lucide-react";

import { Badge } from "~/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { useSalesConditions } from "~/hooks/useSalesConditions";
import { cn } from "~/lib/utils";
import { useProfileStore } from "~/store/profileStore";
import { Client } from "~/types/client";
import { formatCurrency } from "~/utils/helpers";

export interface ClientsTableProps {
  clients: Client[];
  onSort: (field: string) => void;
  sortField: string;
  sortDirection: "asc" | "desc";
}

const ClientsTable: React.FC<ClientsTableProps> = ({
  clients,
  onSort,
  sortField,
  sortDirection,
}) => {
  const navigate = useNavigate();
  const { role } = useProfileStore();
  const isSeller = role === "seller";
  const isMobile = window.innerWidth < 768;
  const { salesConditions, getConditionById } = useSalesConditions();

  // Handle row click to navigate to client detail
  const handleRowClick = (clientId: string | number) => {
    navigate(`/clients/${clientId}`);
  };

  const renderSortIndicator = (field: string) => {
    if (field === sortField) {
      return sortDirection === "asc" ? (
        <ChevronUp className="h-4 w-4" />
      ) : (
        <ChevronDown className="h-4 w-4" />
      );
    }
    return (
      <span className="inline-flex flex-col opacity-50">
        <ChevronUp className="-mb-1 h-3 w-3" />
        <ChevronDown className="h-3 w-3" />
      </span>
    );
  };

  // Helper function to get sales condition name
  const getSalesConditionName = (salesConditionId?: string) => {
    if (!salesConditionId) return "No asignada";

    const condition = getConditionById(salesConditionId);
    return condition ? condition.name : "No encontrada";
  };

  // Mobile card view for sellers
  if (isSeller && isMobile) {
    if (clients.length === 0) {
      return (
        <div className="p-8 text-center text-muted-foreground">
          No se encontraron clientes que coincidan con los criterios de
          búsqueda.
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 gap-3 p-3">
        {clients.map((client, index) => (
          <div
            key={client.id}
            onClick={() => handleRowClick(client.id)}
            style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
            className="cursor-pointer rounded-lg border bg-white p-4 shadow-sm transition-colors duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both hover:bg-slate-50"
          >
            <div className="mb-3 flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-medium">{client.name}</h3>
                  {client.deleted_at && (
                    <Badge variant="destructive" className="text-xs">
                      Eliminado
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  RUT: {client.taxId || "-"}
                </p>
              </div>
              <Badge
                className={cn(
                  "flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium",
                  client.risk_checked
                    ? "border border-green-300 bg-green-100 text-green-800"
                    : "border border-red-300 bg-red-100 text-red-800"
                )}
              >
                {client.risk_checked ? (
                  <ShieldCheck className="h-3 w-3" />
                ) : (
                  <ShieldAlert className="h-3 w-3" />
                )}
                <span>{client.risk_checked ? "Verificado" : "Pendiente"}</span>
              </Badge>
            </div>

            <div className="mb-3 flex flex-col space-y-1 text-sm">
              {client.email && (
                <div className="flex items-center text-gray-600">
                  <Mail className="mr-2 h-3.5 w-3.5" />
                  <span className="truncate">{client.email}</span>
                </div>
              )}
              {client.phone && (
                <div className="flex items-center text-gray-600">
                  <Phone className="mr-2 h-3.5 w-3.5" />
                  <span>{client.phone}</span>
                </div>
              )}
              <div className="flex items-center text-gray-600">
                <User className="mr-2 h-3.5 w-3.5" />
                <span>{client.sellerName}</span>
              </div>
            </div>

            <div className="flex justify-between border-t pt-2 text-sm">
              <div>
                <span className="text-gray-500">Monto Asegurado:</span>
                <div className="font-semibold">
                  {formatCurrency(client.insuredAmount || 0)}
                </div>
              </div>
              <div className="text-right">
                <span className="text-gray-500">Plazo de Pago:</span>
                <div className="font-semibold">
                  {client.trailingPaymentDays || 0} días
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Default desktop table view
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead
            className="w-[180px] cursor-pointer"
            onClick={() => onSort("name")}
          >
            <div className="flex items-center gap-1">
              Cliente
              {renderSortIndicator("name")}
            </div>
          </TableHead>
          <TableHead
            className="w-[110px] cursor-pointer"
            onClick={() => onSort("taxId")}
          >
            <div className="flex items-center gap-1">
              RUT
              {renderSortIndicator("taxId")}
            </div>
          </TableHead>
          <TableHead className="w-[180px]">Email</TableHead>
          <TableHead className="w-[110px]">Teléfono</TableHead>
          <TableHead
            className="w-[150px] cursor-pointer"
            onClick={() => onSort("sellerName")}
          >
            <div className="flex items-center gap-1">
              Vendedor Asignado
              {renderSortIndicator("sellerName")}
            </div>
          </TableHead>
          <TableHead
            className="hidden w-[130px] cursor-pointer text-right md:table-cell"
            onClick={() => onSort("insuredAmount")}
          >
            <div className="flex items-center justify-end gap-1">
              Monto Asegurado
              {renderSortIndicator("insuredAmount")}
            </div>
          </TableHead>
          <TableHead
            className="w-[110px] cursor-pointer"
            onClick={() => onSort("trailingPaymentDays")}
          >
            <div className="flex items-center gap-1">
              Plazo de Pago
              {renderSortIndicator("trailingPaymentDays")}
            </div>
          </TableHead>
          <TableHead
            className="w-[130px] cursor-pointer"
            onClick={() => onSort("risk_checked")}
          >
            <div className="flex items-center gap-1">
              Estado de Riesgo
              {renderSortIndicator("risk_checked")}
            </div>
          </TableHead>
          <TableHead
            className="w-[140px] cursor-pointer"
            onClick={() => onSort("salesConditionId")}
          >
            <div className="flex items-center gap-1">
              Condición de Venta
              {renderSortIndicator("salesConditionId")}
            </div>
          </TableHead>
          {!isSeller && <TableHead className="w-[100px]">Estado</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {clients.length === 0 ? (
          <TableRow>
            <TableCell
              colSpan={isSeller ? 9 : 10}
              className="py-8 text-center text-muted-foreground"
            >
              No se encontraron clientes que coincidan con los criterios de
              búsqueda.
            </TableCell>
          </TableRow>
        ) : (
          clients.map((client, index) => (
            <TableRow
              key={client.id}
              style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
              className="cursor-pointer duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
              onClick={() => handleRowClick(client.id)}
            >
              <TableCell>{client.name}</TableCell>
              <TableCell>{client.taxId || "-"}</TableCell>
              <TableCell>{client.email}</TableCell>
              <TableCell>{client.phone}</TableCell>
              <TableCell>{client.sellerName}</TableCell>
              <TableCell className="hidden text-right md:table-cell">
                {formatCurrency(client.insuredAmount || 0)}
              </TableCell>
              <TableCell>{client.trailingPaymentDays || 0} días</TableCell>
              <TableCell>
                <div className="flex items-center">
                  <Badge
                    className={cn(
                      "flex items-center gap-1 rounded-full px-2 py-1 font-medium",
                      client.risk_checked
                        ? "border border-green-300 bg-green-100 text-green-800"
                        : "border border-red-300 bg-red-100 text-red-800"
                    )}
                  >
                    {client.risk_checked ? (
                      <ShieldCheck className="h-3 w-3" />
                    ) : (
                      <ShieldAlert className="h-3 w-3" />
                    )}
                    <span>
                      {client.risk_checked ? "Verificado" : "Pendiente"}
                    </span>
                  </Badge>
                </div>
              </TableCell>
              <TableCell>
                {getSalesConditionName(client.salesConditionId)}
              </TableCell>
              {!isSeller && (
                <TableCell>
                  {client.deleted_at ? (
                    <Badge variant="destructive" className="text-xs">
                      Eliminado
                    </Badge>
                  ) : (
                    <Badge
                      variant={
                        (client.is_active ?? client.isActive)
                          ? "default"
                          : "secondary"
                      }
                      className="text-xs"
                    >
                      {(client.is_active ?? client.isActive)
                        ? "Activo"
                        : "Inactivo"}
                    </Badge>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
};

export default ClientsTable;
